import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import {
  fetchCompanyById,
  fetchAllEmployees,
  updateCompanyStatus,
} from "../../store/slices/userSlice";

import Breadcrumb from "../../components/navigation/Bredcrumbs";
import DetailTopPart from "../../components/popups/DetailTopPart";
import EmployeeListTable from "../../components/tables/EmployeListTable";
import FilterTopBar from "../../components/navigation/FilterTopBar";
import CompanyActivationPopup from "../../components/popups/CompanyActivationPopup";

import user_img_1 from "../../assets/images/User.png";
import plus_icon from "../../assets/images/plus_icon.svg";
import payroll_icon from "../../assets/images/filter_list_icon.svg";

const CompanyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const COMPANY_DETAIL_KEY = `companyDetail_${id}`;
  const COMPANY_EMPLOYEES_KEY = `companyEmployees_${id}`;

  const selectedUser = useAppSelector(
    (s) => s.user.entityByKey[COMPANY_DETAIL_KEY] || null,
  );

  const loadingSelectedUser = useAppSelector(
    (s) => s.user.entityLoadingByKey[COMPANY_DETAIL_KEY] || false,
  );

  const employeeList = useAppSelector(
    (s) => s.user.listDataByKey[COMPANY_EMPLOYEES_KEY] || [],
  );

  const loadingEmployees = useAppSelector(
    (s) => s.user.listLoadingByKey[COMPANY_EMPLOYEES_KEY] || false,
  );

  const totalRecords = useAppSelector(
    (s) => s.user.totalByKey[COMPANY_EMPLOYEES_KEY] || 0,
  );

  const { exportCSVLoading } = useAppSelector((s) => s.user);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    payRange: [0, 1000000],
    status: true,
  });

  const getEmployees = async () => {
    const params = {
      companyId: id,
      pageIndex: page - 1,
      pageSize,
      startDate: filters.startDate,
      endDate: filters.endDate,
      status: filters.status,
      minPayAvg: filters.payRange[0],
      maxPayAvg: filters.payRange[1],
      search,
    };

    await dispatch(
      fetchAllEmployees({
        params,
        key: COMPANY_EMPLOYEES_KEY,
      }),
    );
  };

  const [search, setSearch] = useState("");
  const [showActivationPopup, setShowActivationPopup] = useState(false);
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationError, setActivationError] = useState(null);

  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";

  // 🔹 Build Full Address
  const buildFullAddress = (user) => {
    if (!user) return "N/A";

    const parts = [
      user.addressLine,
      user.city,
      user.state,
      user.postalCode,
      user.country,
    ];

    return parts.filter(Boolean).join(", ") || "N/A";
  };

  // 🔹 Fetch Company Detail
  useEffect(() => {
    if (!id) return;
    dispatch(fetchCompanyById({ id, key: COMPANY_DETAIL_KEY }));
  }, [id, dispatch]);

  // 🔹 Fetch Employees
  useEffect(() => {
    if (!id) return;

    const timeout = setTimeout(() => {
      getEmployees();
    }, 400);

    return () => clearTimeout(timeout);
  }, [
    id,
    page,
    search,
    filters.startDate,
    filters.endDate,
    filters.status,
    filters.payRange,
  ]);

  // 🔹 Company Data
  const companyData = useMemo(() => {
    if (!selectedUser) return null;

    return {
      name: selectedUser.name || "N/A",
      email: selectedUser.email || "N/A",
      phone: selectedUser.phone || "N/A",
      address: buildFullAddress(selectedUser),
      image: selectedUser.profile
        ? BASE_URL + selectedUser.profile
        : user_img_1,
    };
  }, [selectedUser]);

  // 🔹 Activate Company
  const handleActivateCompany = async ({
    kotapayCompanyId,
    kotapayPaymentId,
  }) => {
    if (!id) return;

    setActivationLoading(true);
    setActivationError(null);

    try {
      await dispatch(
        updateCompanyStatus({
          companyId: parseInt(id),
          isActive: true,
          kotapayCompanyId,
          kotapayPaymentId,
        }),
      ).unwrap();

      // Refresh company data after successful activation
      dispatch(fetchCompanyById({ id, key: COMPANY_DETAIL_KEY }));

      setShowActivationPopup(false);
    } catch (error) {
      setActivationError(
        error?.message || "Failed to activate company. Please try again.",
      );
    } finally {
      setActivationLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: "Companies", link: "/companies" },
    { label: companyData?.name || "Company Detail", active: true },
  ];

  return (
    <div className="company_detail">
      <Breadcrumb items={breadcrumbItems} />

      {companyData && !selectedUser?.isActive && (
        <div className="alert alert-warning mt-3 d-flex justify-content-between align-items-center">
          <span>
            This company is not verified with Kotapay. Payroll is disabled.
          </span>
          <button
            className="btn btn-warning"
            onClick={() => setShowActivationPopup(true)}
          >
            Activate Company
          </button>
        </div>
      )}

      {/* 🔹 Top Section */}
      {loadingSelectedUser && !companyData ? (
        <div className="p-4 text-center">Loading Company Details...</div>
      ) : companyData ? (
        <DetailTopPart
          client={companyData}
          labels={{
            email: "Email",
            phone: "Contact",
            address: "Address",
          }}
          buttons={{
            payroll: {
              text: "Payroll",
              icon: payroll_icon,
              className: selectedUser?.isActive
                ? "btn btn_primary"
                : "btn btn_primary disabled",
              disabled: !selectedUser?.isActive,
              onClick: () => navigate(`/selectedpayroll/${id}`),
            },
            addEmployee: {
              text: "Add Employee",
              icon: plus_icon,
              className: selectedUser?.isActive
                ? "btn btn_outline_primary ms-auto me-2"
                : "btn btn_outline_primary ms-auto me-2 disabled",
              disabled: !selectedUser?.isActive,
              onClick: () =>
                navigate("/addnewemployee", {
                  state: { id },
                }),
            },
          }}
        />
      ) : (
        <div className="p-4 text-center">Company Not Found</div>
      )}

      <div className="employee_table mt-4">
        <div className="container-fluid">
          <FilterTopBar
            title="Employees"
            count={employeeList.length}
            search={search}
            onSearchChange={setSearch}
            placeholder="Search Employees"
            onFilterChange={setFilters}
            companyId={id}
            exportCSVLoading={exportCSVLoading}
            searchClassName="ms-auto"
          />

          <div className="white_box px_20 py_20 mt_20 border-0">
            {loadingEmployees ? (
              <div className="p-4 text-center">Loading Employees...</div>
            ) : (
              <EmployeeListTable
                data={employeeList}
                isDetailPage={true}
                currentPage={page}
                pageSize={pageSize}
                totalRecords={totalRecords}
                onPageChange={setPage}
                onRefresh={getEmployees}
              />
            )}
          </div>
        </div>
      </div>

      <CompanyActivationPopup
        show={showActivationPopup}
        handleClose={() => {
          setActivationError(null);
          setShowActivationPopup(false);
        }}
        onConfirm={handleActivateCompany}
        isLoading={activationLoading}
        errorMessage={activationError}
      />
    </div>
  );
};

export default CompanyDetailPage;
